'use client'

import { useQuery } from '@tanstack/react-query'

import { fetchEtfFundFlows } from '@/service/etf-flow.api'
import type { EtfFundFlowsRequest } from '@/types/generated/public_api'

export const etfFlowKeys = {
  all: ['etfFlow'] as const,
  fundFlows: (params: EtfFundFlowsRequest) =>
    // `byFund` is normalized to a plain boolean rather than passed through as-is: an
    // aggregate request's `undefined` and an explicit `byFund: false` are the same
    // request and must share one cache entry, not mint two.
    [
      ...etfFlowKeys.all,
      'fundFlows',
      params.asset,
      params.from,
      params.to,
      params.byFund === true,
    ] as const,
}

export function useEtfFundFlows(params: EtfFundFlowsRequest, enabled = true) {
  const fromMs = Number(params.from)
  const toMs = Number(params.to)

  // Parsed numerically rather than compared as strings, and deliberately NOT gated on
  // a truthy `asset`: the empty string is the valid "all four assets combined" case,
  // so `Boolean(params.asset)` would disable exactly that request. `to === '0'` means
  // "now" server-side, which is why it bypasses the ordering check — the only range
  // the server rejects outright is from > to.
  //
  // `byFund` additionally requires a non-empty asset — a combined breakdown is a 400.
  // The primary guard against ever reaching this with `byFund: true, asset: ''` lives
  // in the ETF Flow controls and config restore; this is the last line of defense so a
  // stale or hand-edited config can't fire an invalid request past those.
  const ready =
    Number.isFinite(fromMs) &&
    fromMs > 0 &&
    (params.to === '0' || (Number.isFinite(toMs) && fromMs <= toMs)) &&
    (params.byFund !== true || params.asset !== '')

  return useQuery({
    queryKey: etfFlowKeys.fundFlows(params),
    queryFn: ({ signal }) => fetchEtfFundFlows(params, signal),
    enabled: enabled && ready,
    // The vendor publishes once a day, so there is no interval poll here and no
    // reason for one. React Query only refetches on window focus when a query is
    // stale, so this hour also sets how often returning to the tab re-pulls — which
    // is the right cadence for picking up the in-place revisions the feed makes to
    // days already charted.
    staleTime: 60 * 60 * 1000,
  })
}
